/*
 * CMMS i18n message definitions + enum label helpers, shared across the CMMS features.
 *
 * This module must NOT import from any feature's enums module. The enums modules
 * (cmmsWorkOrders/workOrderEnums, cmmsRecurringSchedules/scheduleEnums) import the
 * label helpers from here, so importing them back creates a cycle that webpack
 * builds happily and then fails at runtime with a TDZ error
 * ("Cannot access 'WorkOrderStatus' before initialization").
 * The maps below therefore key on the raw numeric enum values, documented inline.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms";

const cmmsMessages = defineMessages({
  // common
  add: { id: `${scope}.common.add`, defaultMessage: "Add" },
  edit: { id: `${scope}.common.edit`, defaultMessage: "Edit" },
  delete: { id: `${scope}.common.delete`, defaultMessage: "Delete" },
  save: { id: `${scope}.common.save`, defaultMessage: "Save" },
  cancel: { id: `${scope}.common.cancel`, defaultMessage: "Cancel" },
  close: { id: `${scope}.common.close`, defaultMessage: "Close" },
  confirm: { id: `${scope}.common.confirm`, defaultMessage: "Confirm" },
  retry: { id: `${scope}.common.retry`, defaultMessage: "Try again" },
  errorGeneric: { id: `${scope}.common.errorGeneric`, defaultMessage: "Something went wrong." },
  loading: { id: `${scope}.common.loading`, defaultMessage: "Loading…" },

  // work order
  workOrderTitle: { id: `${scope}.workOrder.title`, defaultMessage: "Work order" },
  workOrderTitlePlural: { id: `${scope}.workOrder.titlePlural`, defaultMessage: "Work orders" },

  statusDraft: { id: `${scope}.workOrder.status.draft`, defaultMessage: "Draft" },
  statusOpen: { id: `${scope}.workOrder.status.open`, defaultMessage: "Open" },
  statusInProgress: { id: `${scope}.workOrder.status.inProgress`, defaultMessage: "In progress" },
  statusCompleted: { id: `${scope}.workOrder.status.completed`, defaultMessage: "Completed" },
  statusClosed: { id: `${scope}.workOrder.status.closed`, defaultMessage: "Closed" },
  statusEscalated: { id: `${scope}.workOrder.status.escalated`, defaultMessage: "Escalated" },

  typeMachineBreakdown: { id: `${scope}.workOrder.type.machineBreakdown`, defaultMessage: "Breakdown" },
  typeMachineProblem: { id: `${scope}.workOrder.type.machineProblem`, defaultMessage: "Problem" },
  typePreventiveMaintenance: { id: `${scope}.workOrder.type.preventiveMaintenance`, defaultMessage: "Preventive maintenance" },

  priorityLow: { id: `${scope}.workOrder.priority.low`, defaultMessage: "Low" },
  priorityMedium: { id: `${scope}.workOrder.priority.medium`, defaultMessage: "Medium" },
  priorityHigh: { id: `${scope}.workOrder.priority.high`, defaultMessage: "High" },
  priorityCritical: { id: `${scope}.workOrder.priority.critical`, defaultMessage: "Critical" },

  actionClaim: { id: `${scope}.workOrder.actions.claim`, defaultMessage: "Claim" },
  actionCheckIn: { id: `${scope}.workOrder.actions.checkIn`, defaultMessage: "Check in" },
  actionCheckOut: { id: `${scope}.workOrder.actions.checkOut`, defaultMessage: "Check out" },
  actionComplete: { id: `${scope}.workOrder.actions.complete`, defaultMessage: "Complete" },
  actionReopen: { id: `${scope}.workOrder.actions.reopen`, defaultMessage: "Reopen" },
  actionEscalate: { id: `${scope}.workOrder.actions.escalate`, defaultMessage: "Escalate" },
  actionReportIssue: { id: `${scope}.workOrder.actions.reportIssue`, defaultMessage: "Report issue" },

  // recurring schedule frequency (FrequencyType: Daily=1 … EveryNDays=5)
  frequencyDaily: { id: `${scope}.frequency.daily`, defaultMessage: "Daily" },
  frequencyWeekly: { id: `${scope}.frequency.weekly`, defaultMessage: "Weekly" },
  frequencyMonthly: { id: `${scope}.frequency.monthly`, defaultMessage: "Monthly" },
  frequencyYearly: { id: `${scope}.frequency.yearly`, defaultMessage: "Yearly" },
  frequencyEveryNDays: { id: `${scope}.frequency.everyNDays`, defaultMessage: "Every N days" },

  // recurring schedule trigger basis (ScheduleTriggerBasis: Calendar=1, RunningTime=2, Quantity=3)
  triggerBasisCalendar: { id: `${scope}.triggerBasis.calendar`, defaultMessage: "Calendar" },
  triggerBasisRunningTime: { id: `${scope}.triggerBasis.runningTime`, defaultMessage: "Running time" },
  triggerBasisQuantity: { id: `${scope}.triggerBasis.quantity`, defaultMessage: "Cycles" },

  // day of week (0 = Sunday, matching the backend)
  daySunday: { id: `${scope}.dayOfWeek.sunday`, defaultMessage: "Sunday" },
  dayMonday: { id: `${scope}.dayOfWeek.monday`, defaultMessage: "Monday" },
  dayTuesday: { id: `${scope}.dayOfWeek.tuesday`, defaultMessage: "Tuesday" },
  dayWednesday: { id: `${scope}.dayOfWeek.wednesday`, defaultMessage: "Wednesday" },
  dayThursday: { id: `${scope}.dayOfWeek.thursday`, defaultMessage: "Thursday" },
  dayFriday: { id: `${scope}.dayOfWeek.friday`, defaultMessage: "Friday" },
  daySaturday: { id: `${scope}.dayOfWeek.saturday`, defaultMessage: "Saturday" },

  // work order task status (WorkOrderTaskStatus: Open=1, InProgress=2, Completed=3).
  // Deliberately separate keys from the work order status above: same English words,
  // but a different enum, so translators can diverge them without breaking either.
  taskStatusOpen: { id: `${scope}.workOrderTask.status.open`, defaultMessage: "Open" },
  taskStatusInProgress: { id: `${scope}.workOrderTask.status.inProgress`, defaultMessage: "In progress" },
  taskStatusCompleted: { id: `${scope}.workOrderTask.status.completed`, defaultMessage: "Completed" },

  // related document link type (DocumentType: PmTemplate=1, Sop=2, Drawing=3, Manual=4, Other=5)
  relatedDocumentPmTemplate: { id: `${scope}.relatedDocument.type.pmTemplate`, defaultMessage: "PM template" },
  relatedDocumentSop: { id: `${scope}.relatedDocument.type.sop`, defaultMessage: "SOP" },
  relatedDocumentDrawing: { id: `${scope}.relatedDocument.type.drawing`, defaultMessage: "Drawing" },
  relatedDocumentManual: { id: `${scope}.relatedDocument.type.manual`, defaultMessage: "Manual" },
  relatedDocumentOther: { id: `${scope}.relatedDocument.type.other`, defaultMessage: "Other" },

  // asset document type (Models/Cmms/AssetDocumentType.cs) — distinct from the
  // RelatedDocumentLink DocumentType enum, see assetDocumentEnums.js.
  assetDocumentManual: { id: `${scope}.assetDocument.type.manual`, defaultMessage: "Manual" },
  assetDocumentDrawing: { id: `${scope}.assetDocument.type.drawing`, defaultMessage: "Drawing" },
  assetDocumentCertificate: { id: `${scope}.assetDocument.type.certificate`, defaultMessage: "Certificate" },
  assetDocumentSop: { id: `${scope}.assetDocument.type.sop`, defaultMessage: "SOP" },
  assetDocumentPhoto: { id: `${scope}.assetDocument.type.photo`, defaultMessage: "Photo" },
  assetDocumentOther: { id: `${scope}.assetDocument.type.other`, defaultMessage: "Other" },

  /*
   * Tool service status (ToolServiceStatusLevel: Ok=1, Warning=2, Overdue=3).
   *
   * Shared rather than local to the tools grid
   * badge render the same reading, and a second copy of these strings is how the two drift.
   *
   * The consumption lines are whole sentences with the numbers as parameters, not a label
   * glued to a value — word order and the position of the unit differ between languages.
   */
  toolStatusColumn: { id: `${scope}.toolStatus.column`, defaultMessage: "Service status" },
  toolStatusLevelOk: { id: `${scope}.toolStatus.level.ok`, defaultMessage: "OK" },
  toolStatusLevelWarning: { id: `${scope}.toolStatus.level.warning`, defaultMessage: "Due soon" },
  toolStatusLevelOverdue: { id: `${scope}.toolStatus.level.overdue`, defaultMessage: "Overdue" },
  toolStatusRatioHours: {
    id: `${scope}.toolStatus.ratioHours`,
    defaultMessage: "{consumed} of {interval} h",
  },
  toolStatusRatioCycles: {
    id: `${scope}.toolStatus.ratioCycles`,
    defaultMessage: "{consumed} of {interval} cycles",
  },
  toolStatusRemainingHours: {
    id: `${scope}.toolStatus.remainingHours`,
    defaultMessage: "{remaining} h left before service",
  },
  toolStatusRemainingCycles: {
    id: `${scope}.toolStatus.remainingCycles`,
    defaultMessage: "{remaining} cycles left before service",
  },
  toolStatusOverdueByHours: {
    id: `${scope}.toolStatus.overdueByHours`,
    defaultMessage: "{over} h past the service interval",
  },
  toolStatusOverdueByCycles: {
    id: `${scope}.toolStatus.overdueByCycles`,
    defaultMessage: "{over} cycles past the service interval",
  },
  toolStatusLastServiced: {
    id: `${scope}.toolStatus.lastServiced`,
    defaultMessage: "Last serviced {date}",
  },
  toolStatusNeverServiced: {
    id: `${scope}.toolStatus.neverServiced`,
    defaultMessage: "Never serviced — counting from {date}",
  },
  toolStatusWarnAt: { id: `${scope}.toolStatus.warnAt`, defaultMessage: "Warns at {percent}%" },
  toolStatusNotWatched: {
    id: `${scope}.toolStatus.notWatched`,
    defaultMessage: "No usage-based schedule watches this tool",
  },
  /*
   *
   * Whole sentences with the tool as a parameter, not a label glued to a value: they lead the
   * tooltip and double as the icon's aria-label, so a screen reader gets the meaning and not
   * just a percentage.
   */
  toolStatusBadgeWarning: {
    id: `${scope}.toolStatus.badgeWarning`,
    defaultMessage: "Tool {tool} is due for service soon",
  },
  toolStatusBadgeOverdue: {
    id: `${scope}.toolStatus.badgeOverdue`,
    defaultMessage: "Tool {tool} is past its service interval",
  },

  /*
   *
   * Kept beside the status keys rather than in features/tools because the panel lives in
   * features/cmms and is presentational: /tools mounts it today, and the technician who
   * performs the service cannot reach that page, so a second mount point is expected.
   *
   * `atService` names both units in one sentence because a log row records both, and which of
   * them matters depends on a schedule that may be changed after the service was booked.
   * `workOrder` is a message and not a hard-coded "WO #" — the abbreviation differs by
   * language, and Swedish says AO.
   */
  toolHistoryColumn: { id: `${scope}.toolHistory.column`, defaultMessage: "History" },
  toolHistoryTitle: { id: `${scope}.toolHistory.title`, defaultMessage: "Service history" },
  toolHistoryAction: {
    id: `${scope}.toolHistory.action`,
    defaultMessage: "Service history for {tool}",
  },
  toolHistoryAtService: {
    id: `${scope}.toolHistory.atService`,
    defaultMessage: "{hours} h · {cycles} cycles at service",
  },
  toolHistoryServicedBy: {
    id: `${scope}.toolHistory.servicedBy`,
    defaultMessage: "Serviced by {user}",
  },
  toolHistoryWorkOrder: { id: `${scope}.toolHistory.workOrder`, defaultMessage: "WO #{id}" },
  toolHistoryEmpty: { id: `${scope}.toolHistory.empty`, defaultMessage: "No service recorded" },
  toolHistoryEmptyHint: {
    id: `${scope}.toolHistory.emptyHint`,
    defaultMessage: "A service is recorded when a work order naming this tool is closed.",
  },
  toolHistoryLoadFailed: {
    id: `${scope}.toolHistory.loadFailed`,
    defaultMessage: "Could not load the service history.",
  },

  // stock movement types (StockMovementType: Receipt=1, Adjustment=3, Return=5, Stocktake=6)
  //
  // Here rather than in cmmsSpareParts/messages.js because two features name them: the
  stockMovementReceipt: { id: `${scope}.stockMovement.type.receipt`, defaultMessage: "Goods received" },
  stockMovementAdjustment: { id: `${scope}.stockMovement.type.adjustment`, defaultMessage: "Adjustment" },
  stockMovementReturn: { id: `${scope}.stockMovement.type.return`, defaultMessage: "Return" },
  stockMovementStocktake: { id: `${scope}.stockMovement.type.stocktake`, defaultMessage: "Stocktake" },

  /*
   * Resetting a usage schedule's counter.
   *
   * Here rather than in cmmsRecurringSchedules/messages.js because two features mount the
   * control: the schedules page and the CMMS tool register. The drawer's own chrome stays in
   * the schedule feature's file; what the shared button and the shared log panel say lives here.
   */
  resetAction: { id: `${scope}.reset.action`, defaultMessage: "Reset counter" },
  resetConfirmTitle: { id: `${scope}.reset.confirmTitle`, defaultMessage: "Reset counter" },
  resetConfirmSchedule: {
    id: `${scope}.reset.confirmSchedule`,
    defaultMessage: "Reset the counter on \"{title}\"?",
  },
  resetConfirmButton: { id: `${scope}.reset.confirmButton`, defaultMessage: "Reset" },
  resetNoteLabel: { id: `${scope}.reset.noteLabel`, defaultMessage: "Note (optional)" },
  resetNoteHint: {
    id: `${scope}.reset.noteHint`,
    defaultMessage: "A manual reset has no work order to explain it. Say what was done.",
  },
  resetToastDone: { id: `${scope}.reset.toastDone`, defaultMessage: "Counter reset" },
  resetFailed: { id: `${scope}.reset.failed`, defaultMessage: "Could not reset the counter" },

  // The unit spelled out, so a bare number never has to carry its meaning by position.
  resetCyclesValue: { id: `${scope}.reset.cyclesValue`, defaultMessage: "{cycles} cycles" },
  resetReadingHours: {
    id: `${scope}.reset.readingHours`,
    defaultMessage: "{consumed} of {interval} h",
  },
  resetReadingCycles: {
    id: `${scope}.reset.readingCycles`,
    defaultMessage: "{consumed} of {interval} cycles",
  },

  /*
   * What is about to be discarded. Four variants rather than one string with optional parts: a
   * missing interval and a missing reading are different answers, and neither may be rendered as
   * a zero.
   */
  resetWarningHours: {
    id: `${scope}.reset.warningHours`,
    defaultMessage: "{consumed} counted against the {interval} h interval stops counting toward the next service.",
  },
  resetWarningCycles: {
    id: `${scope}.reset.warningCycles`,
    defaultMessage: "{consumed} counted against the {interval} cycle interval stops counting toward the next service.",
  },
  resetWarningNoInterval: {
    id: `${scope}.reset.warningNoInterval`,
    defaultMessage: "{consumed} counted so far stops counting toward the next service.",
  },
  resetWarningNoReading: {
    id: `${scope}.reset.warningNoReading`,
    defaultMessage: "The consumption counted so far stops counting toward the next service.",
  },

  // Where the figure is counted from. Never IntervalStartingDate — see scheduleReset.js.
  resetSinceLastReset: {
    id: `${scope}.reset.sinceLastReset`,
    defaultMessage: "Counted since the last reset on {date}.",
  },
  resetSinceLastService: {
    id: `${scope}.reset.sinceLastService`,
    defaultMessage: "Counted since the last service on {date}.",
  },
  resetSinceStart: {
    id: `${scope}.reset.sinceStart`,
    defaultMessage: "Counted since this schedule started. No reset has been recorded yet.",
  },

  // the reset log
  resetLogTitle: { id: `${scope}.reset.logTitle`, defaultMessage: "Reset history" },
  resetAtReading: {
    id: `${scope}.reset.atReading`,
    defaultMessage: "{hours} h · {cycles} cycles at reset",
  },
  resetBy: { id: `${scope}.reset.by`, defaultMessage: "Reset by {user}" },
  resetLogEmpty: { id: `${scope}.reset.logEmpty`, defaultMessage: "No resets recorded" },
  resetLogEmptyHint: {
    id: `${scope}.reset.logEmptyHint`,
    defaultMessage: "Resets are recorded from now on — a counter put back to zero before this was released left no entry.",
  },
  resetLogLoadFailed: {
    id: `${scope}.reset.logLoadFailed`,
    defaultMessage: "Could not load the reset history.",
  },

  // ServiceResetSource: WorkOrderClosed=1, Manual=2, ThresholdSweep=3.
  resetSourceWorkOrderClosed: {
    id: `${scope}.reset.source.workOrderClosed`,
    defaultMessage: "Work order closed",
  },
  resetSourceManual: { id: `${scope}.reset.source.manual`, defaultMessage: "Reset by hand" },
  resetSourceThresholdSweep: {
    id: `${scope}.reset.source.thresholdSweep`,
    defaultMessage: "Work order raised automatically",
  },

  /*
   * The estimate (AB#1618). Here rather than in a feature's file because two forms enter it, the
   * work order's and the schedule's, and both have to say the same thing about a typo in it.
   */
  estimatedHoursLabel: { id: `${scope}.estimatedHours.label`, defaultMessage: "Estimated time (h)" },
  estimatedHoursInvalid: {
    id: `${scope}.estimatedHours.invalid`,
    defaultMessage: "Enter the hours as a number, e.g. 2.5",
  },
  estimatedHoursRange: {
    id: `${scope}.estimatedHours.range`,
    defaultMessage: "Between 0 and {max} hours",
  },

  // asset node
  assetNodeTitle: { id: `${scope}.assetNode.title`, defaultMessage: "Asset" },
  assetNodeTitlePlural: { id: `${scope}.assetNode.titlePlural`, defaultMessage: "Assets" },
  assetTypeSite: { id: `${scope}.assetNode.type.site`, defaultMessage: "Site" },
  assetTypeDepartment: { id: `${scope}.assetNode.type.department`, defaultMessage: "Department" },
  assetTypeMaintenanceObject: { id: `${scope}.assetNode.type.maintenanceObject`, defaultMessage: "Maintenance object" },
});

export default cmmsMessages;

// --- enum -> message maps ---
// WorkOrderStatus: Draft=1, Open=2, InProgress=3, Completed=4, Closed=5, Escalated=6.
const workOrderStatusMessages = {
  1: cmmsMessages.statusDraft,
  2: cmmsMessages.statusOpen,
  3: cmmsMessages.statusInProgress,
  4: cmmsMessages.statusCompleted,
  5: cmmsMessages.statusClosed,
  6: cmmsMessages.statusEscalated,
};

// WorkOrderType: MachineBreakdown=1, MachineProblem=2, PreventiveMaintenance=3.
const workOrderTypeMessages = {
  1: cmmsMessages.typeMachineBreakdown,
  2: cmmsMessages.typeMachineProblem,
  3: cmmsMessages.typePreventiveMaintenance,
};

// WorkOrderPriority: Low=1, Medium=2, High=3, Critical=4.
const workOrderPriorityMessages = {
  1: cmmsMessages.priorityLow,
  2: cmmsMessages.priorityMedium,
  3: cmmsMessages.priorityHigh,
  4: cmmsMessages.priorityCritical,
};

// AssetNode.nodeType: 1=Site, 2=Department, 3=Maintenance object (see assetHierarchyUtils).
const assetNodeTypeMessages = {
  1: cmmsMessages.assetTypeSite,
  2: cmmsMessages.assetTypeDepartment,
  3: cmmsMessages.assetTypeMaintenanceObject,
};

const labelFrom = (map, key, intl) => {
  const message = map[key];
  return message ? intl.formatMessage(message) : String(key ?? "");
};

// AssetDocumentType: Manual=1, Drawing=2, Certificate=3, Sop=4, Photo=5, Other=6.
const assetDocumentTypeMessages = {
  1: cmmsMessages.assetDocumentManual,
  2: cmmsMessages.assetDocumentDrawing,
  3: cmmsMessages.assetDocumentCertificate,
  4: cmmsMessages.assetDocumentSop,
  5: cmmsMessages.assetDocumentPhoto,
  6: cmmsMessages.assetDocumentOther,
};

export const getAssetDocumentTypeLabel = (type, intl) => labelFrom(assetDocumentTypeMessages, type, intl);

// WorkOrderTaskStatus: Open=1, InProgress=2, Completed=3.
const taskStatusMessages = {
  1: cmmsMessages.taskStatusOpen,
  2: cmmsMessages.taskStatusInProgress,
  3: cmmsMessages.taskStatusCompleted,
};

export const getTaskStatusLabel = (status, intl) => labelFrom(taskStatusMessages, status, intl);

// RelatedDocumentLink DocumentType: PmTemplate=1, Sop=2, Drawing=3, Manual=4, Other=5.
const relatedDocumentTypeMessages = {
  1: cmmsMessages.relatedDocumentPmTemplate,
  2: cmmsMessages.relatedDocumentSop,
  3: cmmsMessages.relatedDocumentDrawing,
  4: cmmsMessages.relatedDocumentManual,
  5: cmmsMessages.relatedDocumentOther,
};

export const getRelatedDocumentTypeLabel = (type, intl) => labelFrom(relatedDocumentTypeMessages, type, intl);

// FrequencyType: Daily=1, Weekly=2, Monthly=3, Yearly=4, EveryNDays=5.
const frequencyMessages = {
  1: cmmsMessages.frequencyDaily,
  2: cmmsMessages.frequencyWeekly,
  3: cmmsMessages.frequencyMonthly,
  4: cmmsMessages.frequencyYearly,
  5: cmmsMessages.frequencyEveryNDays,
};

export const getFrequencyTypeLabel = (type, intl) => labelFrom(frequencyMessages, type, intl);

// ScheduleTriggerBasis: Calendar=1, RunningTime=2, Quantity=3.
const triggerBasisMessages = {
  1: cmmsMessages.triggerBasisCalendar,
  2: cmmsMessages.triggerBasisRunningTime,
  3: cmmsMessages.triggerBasisQuantity,
};

export const getTriggerBasisLabel = (basis, intl) => labelFrom(triggerBasisMessages, basis, intl);

// ServiceResetSource: WorkOrderClosed=1, Manual=2, ThresholdSweep=3.
//
// Stored on the row, not worked out from what is absent. A null work order id on a tool entry
// would look like the same question and is not — that FK is SetNull on delete, so deleting an
// order would rewrite a recorded service into a manual reset.
const serviceResetSourceMessages = {
  1: cmmsMessages.resetSourceWorkOrderClosed,
  2: cmmsMessages.resetSourceManual,
  3: cmmsMessages.resetSourceThresholdSweep,
};

export const getServiceResetSourceLabel = (source, intl) =>
  labelFrom(serviceResetSourceMessages, source, intl);

// ToolServiceStatusLevel: Ok=1, Warning=2, Overdue=3.
const toolServiceLevelMessages = {
  1: cmmsMessages.toolStatusLevelOk,
  2: cmmsMessages.toolStatusLevelWarning,
  3: cmmsMessages.toolStatusLevelOverdue,
};

export const getToolServiceLevelLabel = (level, intl) => labelFrom(toolServiceLevelMessages, level, intl);

// StockMovementType: Receipt=1, Adjustment=3, Return=5, Stocktake=6.
//
// 2 (Issue) and 4 (Transfer) are absent on purpose and not gaps to fill: consumption is booked
// as MaterialUsage on the work order, and a transfer needs a second location where the register
// has a single StorageLocation field. See Models/Cmms/StockMovementType.cs.
//
// All four are here, Stocktake included, even though only three can be created from the UI —
// the ledger has to render a row of a type its own form cannot produce.
const stockMovementTypeMessages = {
  1: cmmsMessages.stockMovementReceipt,
  3: cmmsMessages.stockMovementAdjustment,
  5: cmmsMessages.stockMovementReturn,
  6: cmmsMessages.stockMovementStocktake,
};

export const getStockMovementTypeLabel = (type, intl) => labelFrom(stockMovementTypeMessages, type, intl);

// Day of week, 0 = Sunday to match the backend's numbering.
const dayOfWeekMessages = {
  0: cmmsMessages.daySunday,
  1: cmmsMessages.dayMonday,
  2: cmmsMessages.dayTuesday,
  3: cmmsMessages.dayWednesday,
  4: cmmsMessages.dayThursday,
  5: cmmsMessages.dayFriday,
  6: cmmsMessages.daySaturday,
};

export const getDayOfWeekLabel = (day, intl) => labelFrom(dayOfWeekMessages, day, intl);

export const getWorkOrderStatusLabel = (status, intl) => labelFrom(workOrderStatusMessages, status, intl);
export const getWorkOrderTypeLabel = (type, intl) => labelFrom(workOrderTypeMessages, type, intl);
export const getWorkOrderPriorityLabel = (priority, intl) => labelFrom(workOrderPriorityMessages, priority, intl);
export const getAssetNodeTypeLabel = (nodeType, intl) => labelFrom(assetNodeTypeMessages, Number(nodeType), intl);
